import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import useFeedback from '../hooks/useFeedback';
import { FaStar, FaStarHalfAlt, FaSpinner, FaTimes } from 'react-icons/fa';

const styles = {
  productFeedback: {
    maxWidth: '600px',
    width: '100%',
    margin: '1rem 0',
    padding: '1.2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    border: 'none',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    '@media (max-width: 768px)': {
      padding: '1rem',
      borderRadius: '8px',
    },
    '@media (max-width: 480px)': {
      padding: '0.8rem',
      margin: '0.5rem 0',
    },
  },
  feedbackContainer: {
    width: '100%',
  },
  averageRatingSection: {
    textAlign: 'left',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid rgba(0, 0, 0, 0.05)',
    '@media (max-width: 480px)': {
      marginBottom: '0.8rem',
      paddingBottom: '0.8rem',
    },
  },
  averageRatingSectionH2: {
    color: '#2d3436',
    fontSize: '1.2rem',
    marginBottom: '0.3rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    '@media (max-width: 480px)': {
      fontSize: '1.1rem',
    },
  },
  ratingSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.8rem',
    flexWrap: 'wrap',
    '@media (max-width: 480px)': {
      gap: '0.5rem',
    },
  },
  ratingStars: {
    display: 'flex',
    gap: '0.3rem',
    background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    '@media (max-width: 480px)': {
      padding: '0.3rem 0.6rem',
    },
  },
  star: {
    fontSize: '1.2rem',
    color: '#dfe6e9',
    width: '1.2rem',
    height: '1.2rem',
    transition: 'transform 0.2s ease',
    '@media (max-width: 480px)': {
      fontSize: '1rem',
      width: '1rem',
      height: '1rem',
    },
  },
  starActive: {
    color: '#ffd700',
    filter: 'drop-shadow(0 2px 2px rgba(255, 215, 0, 0.3))',
  },
  starHalf: {
    fontSize: '1.2rem',
    color: '#ffd700',
    width: '1.2rem',
    height: '1.2rem',
    filter: 'drop-shadow(0 2px 2px rgba(255, 215, 0, 0.3))',
    '@media (max-width: 480px)': {
      fontSize: '1rem',
      width: '1rem',
      height: '1rem',
    },
  },
  ratingDetailsH3: {
    fontSize: '2rem',
    color: '#2d3436',
    margin: 0,
    fontWeight: '700',
    background: 'linear-gradient(45deg, #2d3436 0%, #636e72 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '@media (max-width: 480px)': {
      fontSize: '1.8rem',
    },
  },
  ratingDetailsP: {
    color: '#636e72',
    margin: '0.3rem 0 0',
    fontSize: '0.9rem',
    fontWeight: '500',
    '@media (max-width: 480px)': {
      fontSize: '0.8rem',
    },
  },
  feedbackListSection: {
    marginTop: '1rem',
    '@media (max-width: 480px)': {
      marginTop: '0.8rem',
    },
  },
  feedbackListSectionH3: {
    color: '#2d3436',
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: '600',
    '@media (max-width: 480px)': {
      fontSize: '1rem',
      marginBottom: '0.4rem',
    },
  },
  feedbackItem: {
    padding: '1rem',
    border: 'none',
    borderRadius: '10px',
    marginBottom: '0.8rem',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
    },
    '@media (max-width: 480px)': {
      padding: '0.8rem',
      marginBottom: '0.6rem',
      borderRadius: '8px',
    },
  },
  feedbackContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    '@media (max-width: 480px)': {
      gap: '0.4rem',
    },
  },
  feedbackRating: {
    display: 'flex',
    gap: '0.2rem',
  },
  noReviews: {
    textAlign: 'center',
    padding: '2rem 1rem',
    background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
    borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    '@media (max-width: 480px)': {
      padding: '1.5rem 0.8rem',
      borderRadius: '8px',
    },
  },
  noReviewsH3: {
    color: '#2d3436',
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: '600',
    '@media (max-width: 480px)': {
      fontSize: '1rem',
      marginBottom: '0.4rem',
    },
  },
  noReviewsP: {
    color: '#636e72',
    fontSize: '0.9rem',
    lineHeight: '1.5',
    '@media (max-width: 480px)': {
      fontSize: '0.8rem',
    },
  },
  feedbackAuthor: {
    color: '#2d3436',
    fontSize: '0.9rem',
    margin: 0,
    fontWeight: '600',
    '@media (max-width: 480px)': {
      fontSize: '0.8rem',
    },
  },
  feedbackComment: {
    color: '#636e72',
    fontSize: '0.9rem',
    margin: 0,
    lineHeight: '1.5',
    '@media (max-width: 480px)': {
      fontSize: '0.8rem',
    },
  },
  seeMoreButton: {
    display: 'block',
    width: '100%',
    padding: '0.7rem',
    marginTop: '0.8rem',
    backgroundColor: '#f8f9fa',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    textAlign: 'center',
    textDecoration: 'none',
    color: '#2d3436',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    ':hover': {
      backgroundColor: '#f1f3f5',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    '@media (max-width: 480px)': {
      padding: '0.6rem',
      fontSize: '0.8rem',
      marginTop: '0.6rem',
    },
  },
  feedbackList: {
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(45, 52, 54, 0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem',
    '@media (max-width: 480px)': {
      padding: '0.5rem',
    },
  },
  modalContent: {
    backgroundColor: '#ffffff',
    padding: '1.5rem',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    '@media (max-width: 480px)': {
      padding: '1rem',
      borderRadius: '8px',
      width: '95%',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#636e72',
    transition: 'color 0.2s ease',
    ':hover': {
      color: '#2d3436',
    },
    '@media (max-width: 480px)': {
      fontSize: '1rem',
      top: '6px',
      right: '6px',
    },
  },
};

const ProductFeedback = ({ productId }) => {
  const {
    feedbacks,
    averageRating,
    loading,
    error,
  } = useFeedback(productId);

  const [showOverlay, setShowOverlay] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    console.log('Product ID:', productId);
    console.log('Feedbacks:', feedbacks);
    console.log('Average Rating:', averageRating);
    if (error) {
      setErrorMessage(error);
      setShowError(true);
    }
  }, [productId, feedbacks, averageRating, error]);

  const renderAverageStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={`avg-star-${i}`} style={styles.starActive} size="1.5rem" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={`avg-star-${i}`} style={styles.starHalf} size="1.5rem" />);
      } else {
        stars.push(<FaStar key={`avg-star-${i}`} style={styles.star} size="1.5rem" />);
      }
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="feedback-loading">
        <FaSpinner className="spinner" />
        <p>Loading reviews...</p>
      </div>
    );
  }

  if (!productId) {
    setErrorMessage("No product ID provided");
    setShowError(true);
  }

  const hasReviews = feedbacks && feedbacks.length > 0;

  return (
    <div className="product-feedback" style={styles.productFeedback}>
      <div className="feedback-container" style={styles.feedbackContainer}>
        {/* Average Rating Display */}
        {hasReviews && averageRating && (
          <div className="average-rating-section" style={styles.averageRatingSection}>
            <h2 style={styles.averageRatingSectionH2}>Customer Reviews</h2>
            <div className="rating-summary" style={styles.ratingSummary}>
              <div className="rating-details">
                <h3 style={styles.ratingDetailsH3}>{averageRating.average_rating.toFixed(1)}</h3>
              </div>
              <div className="rating-stars" style={styles.ratingStars}>
                {renderAverageStars(averageRating.average_rating)}
              </div>
              <p style={styles.ratingDetailsP}>Based on {averageRating.total_reviews} reviews</p>
            </div>
          </div>
        )}

        {/* Feedback List */}
        <div className="feedback-list-section" style={styles.feedbackListSection}>
          {!hasReviews ? (
            <div className="no-reviews" style={styles.noReviews}>
              <h3 style={styles.noReviewsH3}>No Reviews Yet</h3>
              <p style={styles.noReviewsP}>This product hasn't received any reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="feedback-list" style={styles.feedbackList}>
              {/* Always show the first feedback if available */}
              {feedbacks[0] && (
                <div key={`feedback-${feedbacks[0].id || 0}`} className="feedback-item" style={styles.feedbackItem}>
                  <div className="feedback-content" style={styles.feedbackContent}>
                    <p className="feedback-author" style={styles.feedbackAuthor}>{feedbacks[0].fullname || 'Anonymous'}</p>
                    <div className="feedback-rating" style={styles.feedbackRating}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={`feedback-${feedbacks[0].id || 0}-star-${star}`}
                          style={star <= (feedbacks[0].rating || 0) ? {...styles.star, ...styles.starActive} : styles.star}
                          size="1rem"
                        />
                      ))}
                    </div>
                    <p className="feedback-comment" style={styles.feedbackComment}>{feedbacks[0].comment || 'No comment'}</p>
                  </div>
                </div>
              )}

              {/* Show See More button if there are more than 1 feedback */}
              {feedbacks.length > 1 && (
                <button
                  className="see-more-button"
                  style={styles.seeMoreButton}
                  onClick={() => setShowOverlay(true)}
                >
                  See More ({feedbacks.length - 1} more)
                </button>
              )}
            </div>
          )}
        </div>

        {/* Feedback Overlay Modal */}
        {showOverlay && (
          <div className="overlay" style={styles.overlay} onClick={() => setShowOverlay(false)}>
            <div className="modal-content" style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button style={styles.closeButton} onClick={() => setShowOverlay(false)}><FaTimes /></button>
              <h3>All Reviews</h3>
              <div className="feedback-list">
                {feedbacks.map((feedback, index) => (
                  <div key={`modal-feedback-${feedback.id || index}`} className="feedback-item" style={styles.feedbackItem}>
                    <div className="feedback-content" style={styles.feedbackContent}>
                      <p className="feedback-author" style={styles.feedbackAuthor}>{feedback.fullname || 'Anonymous'}</p>
                      <div className="feedback-rating" style={styles.feedbackRating}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={`modal-feedback-${feedback.id || index}-star-${star}`}
                            style={star <= (feedback.rating || 0) ? {...styles.star, ...styles.starActive} : styles.star}
                            size="1rem"
                          />
                        ))}
                      </div>
                      <p className="feedback-comment" style={styles.feedbackComment}>{feedback.comment || 'No comment'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showError && (
        <MessageOverlay>
          <ErrorMessage>
            <ErrorIconWrapper>
              <ErrorIcon>✗</ErrorIcon>
            </ErrorIconWrapper>
            <MessageTitle>Error</MessageTitle>
            <MessageDescription>{errorMessage || "An error occurred. Please try again."}</MessageDescription>
            <TryAgainButton onClick={() => setShowError(false)}>Try again</TryAgainButton>
          </ErrorMessage>
        </MessageOverlay>
      )}
    </div>
  );
};

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const MessageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ErrorMessage = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  width: 90%;
  max-width: 350px;
  text-align: center;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease-in;
`;

const ErrorIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #ff3333;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1rem;
`;

const ErrorIcon = styled.span`
  color: white;
  font-size: 2rem;
  font-weight: bold;
`;

const MessageTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
`;

const MessageDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const TryAgainButton = styled.button`
  background: #ff3333;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #cc0000;
  }
`;

export default React.memo(ProductFeedback);
